'use client'

/**
 * The send-feedback modal.
 *
 * ## One rendering path (Issue #13645)
 *
 * This file used to hold four components: a gate that read the session, a
 * real-time-sync provider wrapper, a render-prop bridge into a CRDT document,
 * and a localStorage fallback — selected by a boolean discriminator threaded
 * through a `BodyState` prop so the two could share this JSX. That structure
 * existed because ADR-0061 put a CRDT substrate under this one textarea; it is
 * superseded, and the whole stack (8 npm packages, two API routes, a
 * compaction cron and ~700 lines of glue) is gone.
 *
 * There is now one component. The draft — message, screenshots and meta —
 * belongs to {@link useFeedbackDraft}, which writes through to localStorage
 * and to the `form_drafts` table. Nothing here is stubbed for one path and
 * live for the other: the screenshot controls below are the only
 * implementation, not a branch of one.
 */

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useSyncExternalStore,
} from 'react'
import { useSession } from 'next-auth/react'
import { usePathname, useRouter } from 'next/navigation'
import type { Route } from 'next'
import { toast, toastError } from '@/lib/toast'
import { reportClientError } from '@/lib/client-error'
import { ERROR_IDS } from '@/lib/errors/constants'
import { useFeedbackDraft } from '@/hooks/use-feedback-draft'
import { useUnsavedChanges } from '@/hooks/use-unsaved-changes'
import { useImagePasteUpload } from '@/hooks/use-image-paste-upload'
import { submitFeedback } from '@/app/actions/feedback'
import { uploadScreenshot } from '@/app/actions/feedback-screenshot'
import {
  captureScreenshot,
  isScreenCaptureSupported,
} from '@/lib/screenshot-capture'
import { blobToBase64 } from '@/lib/feedback/blob-to-base64'
import { FEEDBACK_MIN_MESSAGE_LENGTH } from '@/lib/feedback/limits'
import {
  FeedbackMessageTextarea,
  FeedbackMessageCounters,
  FEEDBACK_MESSAGE_INPUT_ID,
} from '@/components/feedback-message-field'
import type { CaptureAttempt } from '@/lib/screenshot-capture'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { Checkbox } from '@/components/ui/checkbox'
import { Modal } from '@/components/ui/modal'
import { usePresentationMode } from '@/contexts/presentation-mode-context'
import { FeedbackType } from '@prisma/client-cadence'
import { isFeedbackType } from '@/lib/feedback-validation'
import {
  getSupportedMimeTypes,
  getSupportedExtensions,
} from '@/lib/storage/file-types'
import { HEIC_ACCEPT } from '@/lib/attachments/heic'
import { trackFeedbackModeEvent } from '@/app/actions/feedback-mode-analytics'
import { FeedbackAnchorRow } from '@/components/feedback/anchor-row'
import {
  FeedbackModalFooter,
  FeedbackShortcutHint,
} from '@/components/feedback/feedback-modal-footer'
import { useFeedbackStartOver } from '@/components/feedback/use-feedback-start-over'
import { useFeedbackMode } from '@/components/feedback/feedback-mode-context'
import {
  feedbackTypes,
  SCREENSHOT_ATTACHMENT_ID,
  SCREEN_CAPTURE_FAILED_MESSAGE,
  RESTORED_SCREENSHOT_DISPLAY,
  type ScreenshotDisplay,
} from '@/components/feedback/feedback-modal-constants'
import type { NormalizedFeedbackAnchor } from '@/lib/feedback/anchor-payload'
import { getActiveFeedbackSessionId } from '@/lib/feedback/active-session'
import {
  PendingSessionRow,
  usePendingSessionAttachment,
} from '@/components/feedback/pending-session-row'
import {
  X,
  Loader2,
  MessageSquarePlus,
  AlertTriangle,
  Paperclip,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  Upload,
  Camera,
} from '@/components/icons'
import { DisclosureChevron } from '@/components/ui/disclosure-chevron'

// ---------------------------------------------------------------------------
// Public prop type
// ---------------------------------------------------------------------------

type FeedbackModalProps = {
  isOpen: boolean
  /**
   * Optional. Why the distinction exists: `use-feedback-entry.ts`.
   *
   * `anchored` is #13756's half: a submission that created a
   * `FeedbackAnchor` must NOT leave feedback mode. The ORIGINAL reason —
   * that the pin it just created was drawn only inside that mode — died with
   * #13788, which made pins an ambient layer; the surviving reason is the one
   * `use-feedback-entry.ts` states, that a viewer who just placed a pin is
   * mid-session rather than finished. A plain submission has no pin to show
   * and keeps the pre-existing exit.
   */
  onClose: (outcome?: { submitted: boolean; anchored?: boolean }) => void
  /** Pre-select feedback type when modal opens */
  initialType?: FeedbackType
  /** Pre-fill message when modal opens */
  initialMessage?: string
  /**
   * Issue #13577 — the element the viewer picked in feedback mode. Absent on
   * every pre-existing call site, and absent is the path that must stay
   * byte-for-byte what shipped.
   */
  anchor?: NormalizedFeedbackAnchor | null
  /** Clears {@link FeedbackModalProps.anchor}; required for it to be removable. */
  onRemoveAnchor?: () => void
}

// getDisplayMedia support never changes within a page, so nothing to subscribe.
const noopSubscribe = () => () => {}

// ---------------------------------------------------------------------------
// FeedbackModal
// ---------------------------------------------------------------------------

export function FeedbackModal({
  isOpen,
  onClose,
  initialType,
  initialMessage,
  anchor,
  onRemoveAnchor,
}: FeedbackModalProps) {
  const pathname = usePathname()
  const router = useRouter()
  const isMobile = useIsMobile()
  /**
   * #13756 — the channel that makes a new pin appear without a reload. Inert
   * outside `FeedbackModeProvider`, which is the right answer for the call
   * sites that are not under the root layout's provider: there is no pin
   * overlay above them to refresh either.
   */
  const { requestAnchorRefresh } = useFeedbackMode()
  const {
    isPresentationMode,
    enable: enablePresentationMode,
    disable: disablePresentationMode,
  } = usePresentationMode()

  // `saveFormDraft`, `getFormDraft`, `uploadScreenshot` and `submitFeedback`
  // are all `withAuth`-composed, so an anonymous visitor gets a working modal
  // with a localStorage-only draft and no guaranteed-failing round trips.
  // That population is real: this modal is mounted by the header on every
  // page, including before sign-in completes.
  const { data: session } = useSession()
  const isAuthenticated = Boolean(session?.user?.id)

  const draft = useFeedbackDraft({ isAuthenticated, isOpen })
  const {
    message,
    screenshots,
    meta,
    setMessage,
    addScreenshot,
    removeScreenshot,
    setMeta,
    clearDraft,
    flushDraft,
  } = draft

  // The selected type lives in the draft's meta, so it resumes with the
  // message rather than resetting every time the modal is reopened.
  //
  // VALIDATED on read, not cast — see `isFeedbackType`'s own doc for why.
  const type = isFeedbackType(meta.feedbackType) ? meta.feedbackType : null
  const setType = useCallback(
    (value: FeedbackType | null) => {
      setMeta({ feedbackType: value ?? '' })
    },
    [setMeta],
  )

  // Warn before navigating away with unsaved feedback
  useUnsavedChanges(isOpen && message.trim().length > 0)

  const [includePageUrl, setIncludePageUrl] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const portalContainerRef = useRef<HTMLDivElement>(null)
  const wasOpenRef = useRef(false)

  // File attachment state (Issue #2877, refactored in #9859)
  // This array holds only non-screenshot file attachments; the screenshot is
  // tracked by the draft (its URL) plus `screenshotDisplay` (its thumbnail).
  const MAX_ATTACHMENTS = 5
  // Error shown when a paste is blocked because the screenshot-inclusive cap
  // is already full (set in handleFeedbackPaste below). Cleared on success.
  const [pasteCapError, setPasteCapError] = useState<string | null>(null)
  const pasteUpload = useImagePasteUpload({
    attachableType: 'Feedback',
    attachableId: 'feedback-pending',
    maxAttachments: MAX_ATTACHMENTS,
  })
  const {
    attachments,
    isUploading,
    error: pasteUploadError,
    onPaste,
    uploadFile,
    removeAttachment: removeFileAttachment,
    clearAttachments,
  } = pasteUpload
  // Screenshot-specific upload error (separate from file/paste upload errors)
  const [screenshotError, setScreenshotError] = useState<string | null>(null)
  // Combined upload error for display: paste-cap and hook errors take
  // precedence over screenshot error.
  const uploadError = pasteUploadError ?? pasteCapError ?? screenshotError
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Collapsible attachments section (Issue #3646)
  const [isAttachmentsExpanded, setIsAttachmentsExpanded] = useState(false)

  // #13788 — the recording a `Stop recording` press just ended.
  const { pendingSession, clearPendingSession } =
    usePendingSessionAttachment(isOpen)

  // Auto-expand attachments section when a paste upload completes (#9859).
  // Mirrors the original uploadFile behaviour that called setIsAttachmentsExpanded(true).
  const attachmentsCount = attachments.length
  useEffect(() => {
    if (attachmentsCount > 0) setIsAttachmentsExpanded(true)
  }, [attachmentsCount])

  // Screenshot capture state (Issue #3646)
  const [isCapturing, setIsCapturing] = useState(false)
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null)
  // `navigator` does not exist on the server: the server snapshot is `false`,
  // so SSR and hydration agree and the button appears after mount (#14858).
  const canCaptureScreen = useSyncExternalStore(
    noopSubscribe,
    isScreenCaptureSupported,
    () => false,
  )
  const captureAttemptsRef = useRef<CaptureAttempt[]>([])

  // Thumbnail/filename/size for the screenshot captured in THIS mount. Only
  // the URL ref is persisted in the draft; display metadata is ephemeral.
  const [screenshotDisplay, setScreenshotDisplay] =
    useState<ScreenshotDisplay | null>(null)

  // #14531 — footer "Start over" and its Undo toast; see the hook's header.
  const { startOver, dismissUndo } = useFeedbackStartOver({
    isOpen,
    isAuthenticated,
    draft,
    files: pasteUpload,
    screenshot: {
      display: screenshotDisplay,
      blob: screenshotBlob,
    },
    setScreenshot: ({ display, blob }) => {
      setScreenshotDisplay(display)
      setScreenshotBlob(blob)
    },
    captureAttemptsRef,
    clearErrors: () => {
      setScreenshotError(null)
      setPasteCapError(null)
    },
  })

  // The screenshot entry, if any: from this mount's capture when there is one,
  // otherwise synthesised from a draft resumed with a screenshot ref — so a
  // refreshed or cross-device draft shows its attachment immediately.
  const screenshotAttachment = useMemo(() => {
    const display =
      screenshotDisplay ??
      (screenshots.length > 0 ? RESTORED_SCREENSHOT_DISPLAY : null)
    return display ? { id: SCREENSHOT_ATTACHMENT_ID, ...display } : null
  }, [screenshotDisplay, screenshots])

  // Effective attachment list used for rendering: file attachments owned by
  // the paste/file-select hook, plus the single screenshot entry.
  const effectiveAttachments = useMemo(() => {
    const nonScreenshot = attachments.filter(
      a => a.id !== SCREENSHOT_ATTACHMENT_ID,
    )
    return screenshotAttachment
      ? [...nonScreenshot, screenshotAttachment]
      : nonScreenshot
  }, [attachments, screenshotAttachment])

  // Sync state when modal opens with new initial values.
  // Seeded only on the transition to open, and only over an empty field, so a
  // resumed draft is never overwritten by a caller's defaults.
  //
  // `meta.pageUrl` is deliberately NOT seeded here, though the CRDT path used
  // to seed it with `window.location.href`: nothing reads it, and a stored
  // draft row that quietly carries a full URL (ids and query included) for no
  // reader is not worth keeping. The field survives in the draft shape so a
  // row written by the old path round-trips instead of being truncated.
  //
  // A caller-supplied `initialType` / `initialMessage` is EXPLICIT INTENT and
  // wins over a resumed draft. Gating these on the field being empty (as an
  // earlier revision of #13645 did) looks like "don't clobber the user's
  // draft", but the only caller that passes `initialMessage` is
  // `error-details-context.tsx`'s Report Bug flow, which composes the error
  // message, its stack and a context JSON blob. With any draft present that
  // flow opened showing the unrelated draft, untyped, with the whole
  // diagnostic payload silently dropped — and nothing told the submitter.
  // `main` seeded unconditionally here; this restores that.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      if (initialType !== undefined) setType(initialType)
      if (initialMessage !== undefined) setMessage(initialMessage)
    }
    wasOpenRef.current = isOpen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialType, initialMessage])

  // Capture a screenshot with getDisplayMedia (Issue #3646). There is no
  // fallback (#14858): unsupported or failed means no screenshot.
  // Enables presentation mode to hide sensitive content (Issue #3771)
  async function handleCaptureScreenshot() {
    if (isCapturing) return

    setIsCapturing(true)
    setScreenshotError(null)

    // Remember if presentation mode was already on so we can
    // restore it after capture (Issue #3771)
    const wasPresentationMode = isPresentationMode

    // Enable presentation mode to blur sensitive content in the
    // screenshot (Issue #3771). React batches this state update
    // and commits before the next browser task, so by the time
    // getDisplayMedia shows its dialog, SensitiveContent components will have re-rendered.
    if (!wasPresentationMode) {
      enablePresentationMode()
    }

    // Hide the modal, backdrop, and presentation mode banner so
    // they don't appear in the screenshot (Issue #3681, #3771)
    const container = portalContainerRef.current
    if (container) {
      container.style.visibility = 'hidden'
    }
    const banner = document.querySelector<HTMLElement>(
      '[data-presentation-banner]',
    )
    if (banner) {
      banner.style.visibility = 'hidden'
    }

    try {
      const result = await captureScreenshot()
      captureAttemptsRef.current.push(result.attempt)

      if (result.screenshot) {
        // Client-side capture succeeded (getDisplayMedia)
        setScreenshotBlob(result.screenshot.blob)
        setScreenshotDisplay({
          fileName: 'screenshot.png',
          mimeType: 'image/png',
          sizeBytes: result.screenshot.blob.size,
          preview: result.screenshot.dataUrl,
        })
        if (isAuthenticated) {
          // Upload now rather than at submit, so the draft can hold a URL
          // ref: a screenshot that only exists as an in-memory Blob does not
          // survive the refresh this draft layer exists to survive. The Blob
          // is kept as the submit-time fallback if this upload fails.
          const base64 = await blobToBase64(result.screenshot.blob)
          const uploadResult = await uploadScreenshot(base64)
          if (uploadResult.success) {
            addScreenshot(uploadResult.url)
          } else {
            setScreenshotError(
              uploadResult.error || 'Failed to upload screenshot',
            )
          }
        }
        setIsAttachmentsExpanded(true)
      } else if (result.attempt.outcome === 'failure') {
        // No fallback (#14858): say so, and point at the paths that remain.
        setScreenshotError(SCREEN_CAPTURE_FAILED_MESSAGE)
        setIsAttachmentsExpanded(true)
      }
      // Cancelled or unsupported: nothing to attach, nothing to say. Every
      // attempt is still recorded above and reported in captureMetadata.
    } catch (err) {
      // blobToBase64 can reject (FileReader.onerror) — surface to the user
      // rather than letting it become an unhandled promise rejection.
      reportClientError({
        errorId: ERROR_IDS.FEEDBACK_SCREENSHOT_UPLOAD_FAILED,
        action: 'captureFeedbackScreenshot',
        error: err,
      })
      setScreenshotError('Failed to process screenshot')
      setIsAttachmentsExpanded(true)
    } finally {
      // Restore modal visibility (Issue #3681)
      if (container) {
        container.style.visibility = ''
      }
      // Restore banner visibility (Issue #3771)
      if (banner) {
        banner.style.visibility = ''
      }
      // Restore original presentation mode state (Issue #3771)
      if (!wasPresentationMode) {
        disablePresentationMode()
      }
      setIsCapturing(false)
    }
  }

  async function handleSubmit() {
    if (
      !type ||
      message.trim().length < FEEDBACK_MIN_MESSAGE_LENGTH ||
      isSubmitting
    ) {
      return
    }

    setIsSubmitting(true)
    try {
      let screenshotUrl: string | undefined
      let screenshotFailed = false

      // Resolve the screenshot to a URL (Issue #3646). Preference order: the
      // draft's persisted ref (uploaded at capture time), then a late upload
      // of the Blob — the latter is the only path left when the capture-time upload
      // failed or there was no session to run it.
      if (screenshotAttachment) {
        if (screenshots.length > 0) {
          screenshotUrl = screenshots[0]
        } else if (screenshotBlob) {
          const base64 = await blobToBase64(screenshotBlob)

          const uploadResult = await uploadScreenshot(base64)
          if (uploadResult.success) {
            screenshotUrl = uploadResult.url
          } else {
            // Surface the error in the modal for the submit-also-fails case.
            // When submitFeedback succeeds, handleClose clears this — the
            // "screenshot not included" toast is the channel for that path.
            setScreenshotError(
              uploadResult.error || 'Failed to upload screenshot',
            )
            screenshotFailed = true
          }
        }
      }

      // Get non-screenshot attachment IDs (file-only, never includes screenshot)
      const fileAttachmentIds = attachments
        .filter(a => a.id !== SCREENSHOT_ATTACHMENT_ID)
        .map(a => a.id)

      // Build capture metadata for analytics (Issue #3646)
      const attempts = captureAttemptsRef.current
      const captureMetadata =
        attempts.length > 0
          ? JSON.parse(
              JSON.stringify({
                screenshotAttempts: attempts,
                uploadOutcome: screenshotAttachment
                  ? screenshotFailed
                    ? 'upload_failed'
                    : 'uploaded'
                  : 'not_included',
              }),
            )
          : undefined

      const result = await submitFeedback({
        // Issue #13577 — the picked element, absent on every pre-existing
        // call site. `submitFeedback` takes this as `unknown` and runs it
        // through `parseFeedbackAnchorInput` before any database work, so the
        // client is not trusted to have built it correctly; `anchor-payload.ts`
        // builds it against the same types that parse validates. #13580's
        // `sessionId` is the same arrangement, re-resolved server-side.
        anchor: anchor ?? undefined,
        // The RUNNING session first, then the one a stop just ended (#13788).
        sessionId:
          getActiveFeedbackSessionId() ??
          pendingSession?.sessionId ??
          undefined,
        type,
        message,
        pageUrl: includePageUrl ? pathname : undefined,
        screenshotUrl,
        attachmentIds: fileAttachmentIds,
        captureMetadata,
      })

      if (result.success) {
        if (anchor) {
          // Emitted only on the SUCCESS path, and only now that the anchor
          // above actually reaches `submitFeedback` (#13574). Against
          // `feedback.submitted` this is the adoption rate of the whole
          // feature, so it must never fire for a submission that stored no
          // anchor — that would report full adoption of a feature that had
          // persisted nothing.
          //
          // Shape only, never the label or the selector path: both can embed
          // page content (see the `selector-path.ts` header), and adoption
          // analytics is a different table from the feedback row.
          void trackFeedbackModeEvent('feedback.anchored_submitted', {
            anchorKind: anchor.anchorKind,
            fragile: anchor.fragile,
            route: anchor.route,
            hasPinRect: anchor.pinRect !== null,
          }).catch(() => {})
          /**
           * #13756 — make the new pin appear without a reload.
           *
           * Inside the `if (anchor)` branch, and inside `result.success`, for
           * the same two reasons the event above is: a plain submission
           * creates no anchor, so a refetch would be a wasted round trip on
           * every feedback the app takes; and a failed submit stored nothing
           * to fetch.
           *
           * This bumps the counter an admin triage already bumped, so the
           * overlay has ONE refetch path. The overlay is mounted in the root
           * layout and this modal is rendered by `horizontal-header.tsx`, so
           * the feedback-mode context is the only channel between them —
           * outside a provider it is inert, which is the right answer for the
           * `/feedback` page's own modal, where there is no overlay to
           * refresh.
           */
          requestAnchorRefresh()
        }
        const feedbackUrl = `/feedback/${result.feedbackId}`
        const baseMessage = screenshotFailed
          ? 'Feedback submitted (screenshot not included)'
          : 'Thank you for your feedback!'

        toast.success(baseMessage, {
          duration: 5000,
          action: {
            label: 'View',
            onClick: () => router.push(feedbackUrl as Route),
          },
        })
        // Issue #5107: clear the draft only on successful submit. One call
        // now empties state, localStorage AND the form_drafts row — before
        // #13645 this was a deleteFormDraft, a clearMessage, a local CRDT
        // compaction and a server-side op-log delete, any of which could
        // leave the others behind.
        clearDraft()
        // `anchor` is read here rather than inside `handleClose`, because
        // that function also runs from the three dismissal paths where there
        // is no submission to describe.
        handleClose({ preserveDraft: false, anchored: Boolean(anchor) })
      } else {
        toastError({
          message: result.error || 'Failed to submit feedback',
          context: { action: 'submitFeedback' },
        })
      }
    } catch (error) {
      reportClientError({
        errorId: ERROR_IDS.FEEDBACK_MODAL_SUBMIT_FAILED,
        action: 'submitFeedback',
        error,
      })
      toastError({
        message: 'Failed to submit feedback. Please try again.',
        context: { action: 'submitFeedback' },
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleClose({ preserveDraft = true, anchored = false } = {}) {
    if (preserveDraft) {
      // Bypass the debounce so a draft abandoned mid-keystroke is already in
      // both stores by the time the modal is gone.
      flushDraft()
    }
    setIncludePageUrl(true)
    dismissUndo()
    clearAttachments()
    setScreenshotError(null)
    setPasteCapError(null)
    setIsAttachmentsExpanded(false)
    setScreenshotBlob(null)
    setScreenshotDisplay(null)
    captureAttemptsRef.current = []
    // #13841 — on SUBMIT only (`preserveDraft: false` IS the 2xx path).
    // Clearing on dismissal too made a cancel a DISCARD; see
    // `usePendingSessionAttachment` for where that protection went instead.
    if (!preserveDraft) clearPendingSession()
    // `preserveDraft: false` is passed only on the 2xx path, so it IS submitted.
    onClose({ submitted: !preserveDraft, anchored })
  }

  // Handle file attachment upload (Issue #2877, refactored in #9859)
  // uploadFile is provided by useImagePasteUpload; expand the section on success.
  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (!files || files.length === 0) return

    // Extract file reference BEFORE clearing input value.
    // Clearing input.value can mutate the FileList per HTML spec,
    // making files[0] undefined and silently breaking the upload.
    const file = files[0]

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    await uploadFile(file)
    setIsAttachmentsExpanded(true)
  }

  function handleRemoveAttachment(attachmentId: string) {
    if (attachmentId === SCREENSHOT_ATTACHMENT_ID) {
      setScreenshotBlob(null)
      setScreenshotDisplay(null)
      // There is at most one screenshot, at index 0. Removing it from the
      // draft is what makes the removal survive a refresh.
      if (screenshots.length > 0) removeScreenshot(0)
      return
    }
    removeFileAttachment(attachmentId)
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const canSubmit =
    type &&
    message.trim().length >= FEEDBACK_MIN_MESSAGE_LENGTH &&
    !isSubmitting
  const totalAttachments = effectiveAttachments.length
  const canUploadMore = effectiveAttachments.length < MAX_ATTACHMENTS
  // Gate the paste handler on effectiveAttachments so that a screenshot
  // tracked outside the hook still counts toward the MAX_ATTACHMENTS cap.
  // Without this, the hook's internal cap only sees hook-managed attachments
  // and would allow MAX_ATTACHMENTS pastes + 1 screenshot = 6 total.
  // Sets pasteCapError (displayed via uploadError) so the user sees feedback.
  const handleFeedbackPaste = useCallback<React.ClipboardEventHandler>(
    e => {
      if (effectiveAttachments.length >= MAX_ATTACHMENTS) {
        setPasteCapError(`Maximum ${MAX_ATTACHMENTS} attachments allowed`)
        return
      }
      setPasteCapError(null)
      onPaste(e)
    },
    [effectiveAttachments.length, onPaste],
  )
  const hasScreenshot = screenshotAttachment !== null
  // Only where getDisplayMedia exists: without it the button does nothing.
  const showCaptureButton = !hasScreenshot && canCaptureScreen
  const attachmentsContentId = 'feedback-attachments-content'

  const footer = (
    <FeedbackModalFooter
      showStartOver={draft.wasRestored}
      onStartOver={startOver}
      startOverDisabled={isUploading || isSubmitting || isCapturing}
      onCancel={() => handleClose()}
      onSubmit={handleSubmit}
      canSubmit={Boolean(canSubmit)}
      isSubmitting={isSubmitting}
    />
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => handleClose()}
      size="md"
      layer="feedback"
      portalRef={portalContainerRef}
      ariaLabelledBy="feedback-modal-title"
      noPadding
      onSubmit={canSubmit ? handleSubmit : null}
      footer={footer}
    >
      <div
        className="overscroll-contain touch-auto"
        onWheel={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-stroke-regular">
          <div data-feedback-modal-header>
            <div className="flex items-center gap-2">
              <MessageSquarePlus
                className="w-5 h-5 text-text-primary"
                aria-hidden="true"
              />
              <h2
                id="feedback-modal-title"
                className="text-lg font-bold text-text-primary"
              >
                Send Feedback
              </h2>
            </div>
            {!isMobile && <FeedbackShortcutHint />}
          </div>
          <button
            onClick={() => handleClose()}
            className="size-8 pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px] inline-flex items-center justify-center rounded-md text-text-secondary hover:text-text-secondary hover:bg-fill-10 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Issue #13577 — present only when feedback mode picked an
              element; absent is the unchanged modal. */}
          {/* Both, not just `anchor`: `onRemoveAnchor` is optional, and
              rendering the row without it produced a remove button that did
              nothing — a dead escape hatch is worse than no visible anchor,
              because the row's whole promise is that it is the way back. */}
          {anchor && onRemoveAnchor && (
            <FeedbackAnchorRow anchor={anchor} onRemove={onRemoveAnchor} />
          )}
          {pendingSession && (
            <PendingSessionRow
              attachment={pendingSession}
              onRemove={clearPendingSession}
            />
          )}
          {/* Feedback Type Selection */}
          <fieldset>
            <legend className="block text-sm font-medium text-text-primary mb-2">
              What type of feedback is this?
            </legend>
            <div className="grid grid-cols-2 gap-2">
              {feedbackTypes.map(
                ({
                  value,
                  label,
                  icon: Icon,
                  description,
                  selectedBg,
                  selectedText,
                  selectedLabel,
                }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setType(value)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      type === value
                        ? selectedBg
                        : 'border-stroke-regular hover:border-stroke-light hover:bg-surface-panel'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon
                        className={`w-4 h-4 ${type === value ? selectedText : 'text-text-secondary'}`}
                      />
                      <span
                        className={`text-sm font-medium ${type === value ? selectedLabel : 'text-text-primary'}`}
                      >
                        {label}
                      </span>
                    </div>
                    <p className="text-xs text-text-secondary">{description}</p>
                  </button>
                ),
              )}
            </div>
          </fieldset>

          {/* Message Input */}
          <div>
            <label
              htmlFor={FEEDBACK_MESSAGE_INPUT_ID}
              className="block text-sm font-medium text-text-primary mb-2"
            >
              Your feedback
            </label>
            <FeedbackMessageTextarea
              message={message}
              setMessage={setMessage}
              onPaste={handleFeedbackPaste}
            />
            <FeedbackMessageCounters message={message} />
          </div>

          {/* Attachments Section - styled like ItemAttachmentsSection (Issue #3646) */}
          <div className="rounded-xl border border-stroke-regular bg-surface-elevated shadow-sm overflow-hidden">
            {/* Attachments Header */}
            <div className="flex items-center px-4 py-3 transition-colors hover:bg-surface-panel group">
              <button
                type="button"
                onClick={() => setIsAttachmentsExpanded(!isAttachmentsExpanded)}
                className="min-h-8 pointer-coarse:min-h-11 flex flex-1 items-center gap-2 text-left"
                aria-expanded={isAttachmentsExpanded}
                aria-controls={attachmentsContentId}
                aria-label={`${isAttachmentsExpanded ? 'Collapse' : 'Expand'} attachments section${totalAttachments > 0 ? `, ${totalAttachments} attachment${totalAttachments === 1 ? '' : 's'}` : ''}`}
              >
                <Paperclip className="w-4 h-4 text-text-secondary" />
                <span className="text-sm font-semibold uppercase tracking-wide text-text-primary">
                  Attachments
                </span>
                {totalAttachments > 0 && (
                  <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-xs font-medium rounded-full bg-fill-10 text-text-secondary">
                    {totalAttachments}
                  </span>
                )}
                <DisclosureChevron
                  open={isAttachmentsExpanded}
                  className="h-4 w-4 text-text-secondary group-hover:text-text-primary"
                />
              </button>

              {/* Header action buttons */}
              <div className="flex items-center gap-1 ml-2">
                {/* Capture Screenshot button — only shown if getDisplayMedia is supported */}
                {showCaptureButton && (
                  <button
                    type="button"
                    data-testid="feedback-capture-screenshot"
                    onClick={e => {
                      e.stopPropagation()
                      handleCaptureScreenshot()
                    }}
                    disabled={isCapturing}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md text-text-secondary hover:bg-fill-10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Capture screenshot"
                  >
                    {isCapturing ? (
                      <Loader2
                        className="h-3.5 w-3.5 animate-spin"
                        aria-hidden="true"
                      />
                    ) : (
                      <Camera className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    <span className="hidden sm:inline">
                      {isCapturing ? 'Capturing...' : 'Screenshot'}
                    </span>
                  </button>
                )}
                {canUploadMore && (
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation()
                      if (!isAttachmentsExpanded) setIsAttachmentsExpanded(true)
                      fileInputRef.current?.click()
                    }}
                    disabled={isUploading}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md text-text-secondary hover:bg-fill-10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Add file"
                  >
                    {isUploading ? (
                      <Loader2
                        className="h-3.5 w-3.5 animate-spin"
                        aria-hidden="true"
                      />
                    ) : (
                      <Upload className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    <span className="hidden sm:inline">
                      {isUploading ? 'Uploading...' : 'Add File'}
                    </span>
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="sr-only"
                aria-label="Attach file"
                accept={[
                  ...getSupportedMimeTypes(),
                  ...getSupportedExtensions(),
                  ...HEIC_ACCEPT,
                ].join(',')}
              />
            </div>

            {/* Attachments Content - collapsible */}
            {isAttachmentsExpanded && (
              <div
                id={attachmentsContentId}
                className="px-4 pb-4 pt-3 space-y-3 border-t border-stroke-extra-light"
              >
                {uploadError && (
                  <div
                    className="flex items-center gap-2 text-sm text-status-amber-text"
                    aria-live="polite"
                  >
                    <AlertTriangle className="w-4 h-4" aria-hidden="true" />
                    {uploadError}
                  </div>
                )}

                {effectiveAttachments.length > 0 && (
                  <div className="space-y-2" aria-live="polite">
                    {effectiveAttachments.map(attachment => (
                      <div
                        key={attachment.id}
                        className="relative group flex items-center gap-3 p-3 bg-surface-panel border border-stroke-regular rounded-lg"
                      >
                        {/* Thumbnail */}
                        <div className="w-12 h-12 flex-shrink-0 rounded-md overflow-hidden bg-fill-10 flex items-center justify-center">
                          {attachment.preview ? (
                            <img
                              src={attachment.preview}
                              alt={attachment.fileName}
                              className="w-full h-full object-cover"
                            />
                          ) : attachment.mimeType.startsWith('image/') ? (
                            <ImageIcon className="w-5 h-5 text-text-secondary" />
                          ) : attachment.mimeType === 'application/pdf' ||
                            attachment.mimeType.includes('document') ? (
                            <FileText className="w-5 h-5 text-text-secondary" />
                          ) : attachment.mimeType.includes('spreadsheet') ||
                            attachment.mimeType === 'text/csv' ? (
                            <FileSpreadsheet className="w-5 h-5 text-text-secondary" />
                          ) : (
                            <FileText className="w-5 h-5 text-text-secondary" />
                          )}
                        </div>

                        {/* File info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-text-primary truncate">
                            {attachment.fileName}
                          </p>
                          <p className="text-xs text-text-secondary">
                            {formatFileSize(attachment.sizeBytes)}
                          </p>
                        </div>

                        {/* Remove button */}
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(attachment.id)}
                          className="p-1.5 text-text-secondary hover:text-status-red  transition-colors"
                          aria-label={`Remove ${attachment.fileName}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {effectiveAttachments.length === 0 && !uploadError && (
                  <p className="text-sm text-text-secondary text-center py-2">
                    Add images, PDFs, or documents to help explain your feedback
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Include Page URL Checkbox — the shared sai-ui `Checkbox` since
              #15037; the wrapping label names it and is the pointer target. */}
          <label
            htmlFor="include-page-url"
            className="flex items-center gap-2 cursor-pointer"
          >
            <Checkbox
              id="include-page-url"
              checked={includePageUrl}
              onCheckedChange={setIncludePageUrl}
            />
            <span className="text-sm text-text-secondary">
              Include current page for context
            </span>
          </label>
        </div>
      </div>
    </Modal>
  )
}
